import type { FC } from 'react'
import { useParams } from 'next/navigation'
import styled from 'styled-components'
import { useTranslator } from '@/hooks'
import { CLIENT_NAME, scrollToTop, SOCIAL_NETWORKS_TRANSLATION } from '@/utils'
import type { TypeLang } from '@/schema'
import { createUnderline, defaultGrid, designGrid, innerGrid, mq, presets } from '@/styles'
import { Img } from '../base'

const SVG_LOGO_VADEVER_WHITE = {
  alt: 'Vadever Logotype',
  type: 'svg' as const,
  src: '/images/svg/logo--vadever--white.svg',
  size: { width: 183, height: 33 },
  content_type: 'image/svg+xml'
}

export const COMPANY_LINKS = {
  googleMaps: 'https://g.co/kgs/HQmrzDR',
  instagram: 'https://www.instagram.com/vadeverstudios/',
}

export const Footer:FC = () => {
  const { lang } = useParams() as { lang: TypeLang }
  const { t } = useTranslator()

  return (
    <FooterStyled>
      <a href='https://vadever.com' className='link--logo' target='_blank' rel='noopener noreferrer' aria-label={`${'Vadever'} (Opens in new tab)`}>
        <Img media={SVG_LOGO_VADEVER_WHITE} />
      </a>
      <address>
        <span>{t('project.footer_message')}</span><br />
        <a href={'https://vadever.com'} target='_blank' rel='noopener noreferrer' aria-label={`${'Google maps'} (Opens in new tab)`}>
          {t('project.visit_web')}
        </a>
      </address>
      <div className='links-wrapper'>
        <nav aria-labelledby='f__other-links'>
          <h2 id='f__other-links' className='srt-only'>Useful Links</h2>
          {/* <ul>
            <li><Link href='/studios'>Faqs</Link></li>
            <li><Link href='/crafting'>Términos y condiciones</Link></li>
            <li><Link href='/streaming-chroma'>Política de privacidad</Link></li>
          </ul> */}
        </nav>
      </div>
      <div>
        <p className='copyright'>
          <span>© {CLIENT_NAME} {new Date().getFullYear()}</span>
        </p>
        <nav aria-labelledby='f__social-media'>
          <h2 id='f__social-media' className='srt-only'>{SOCIAL_NETWORKS_TRANSLATION[lang]}</h2>
          <ul>
            <li>
              <a href={COMPANY_LINKS.instagram} target='_blank' rel='noopener noreferrer' aria-label={`${'Instagram'} (Opens in new tab)`}>
                Instagram
              </a>
            </li>
          </ul>
        </nav>
        <button className='btn btn--scroll-top' onClick={scrollToTop}>
          <svg xmlns="http://www.w3.org/2000/svg" width="11" height="12" viewBox="0 0 11 12" fill="none">
            <path d="M5.5 11.5V0.5" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M1 5L5.5 0.5L10 5" stroke="white" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span>{t('project.back_to_top')}</span>
        </button>
      </div>
    </FooterStyled>
  )
}

const FooterStyled = styled.footer`
  ${designGrid({})}
  /* margin-top: 50px; */
  padding: 40px 0 30px;

  ${mq.greaterThan('tablet')} {
    /* margin-top: 70px; */
  }

  .link--logo {
    grid-column: 1 / span 6;
    grid-row: 1 / span 1;
    margin-bottom: 40px;
    max-width: 182px;

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 3;
      margin-bottom: 0;
      max-width: 232px;
    }
  }

  address {
    ${presets.font30()}
    grid-column: 1 / span 6;
    margin-bottom: 60px;

    ${mq.greaterThan('tablet')} {
      grid-column: 6 / span 4;
      grid-row: 1 / span 1;
      margin-bottom: 0;
    }

    a {
      ${createUnderline({ color: 'var(--color-gold)', reverse: true })}
      color: var(--color-gold);
    }
  }

  address,
  .links-wrapper {
    ${mq.greaterThan('tablet')} {
      margin-bottom: 150px;
    }
  }

  > div {
    grid-column: 1 / span 6;
    ${innerGrid(defaultGrid.mobile)}

    ${mq.greaterThan('tablet')} {
      grid-column: 1 / span 12;
      ${innerGrid(defaultGrid.tablet)}
    }

    ${mq.greaterThan('desktop')} {
      ${innerGrid(defaultGrid.desktop)}
    }

    &:last-child {
      align-items: baseline;

      ${mq.greaterThan('tablet')} {
        grid-row: 2 / span 1;
      }
    }
  }

  .links-wrapper {
    ${mq.lessThan('tablet')} {
      margin-bottom: 120px;
    }

    ${mq.greaterThan('tablet')} {
      grid-row: 1 / span 1;
    }
  }

  nav {
    &[aria-labelledby='f__main-nav'] {
      grid-column: 1 / span 4;

      ${mq.greaterThan('tablet')} {
        grid-column: 6 / span 3;
      }
    }

    &[aria-labelledby='f__main-nav'],
    &[aria-labelledby='f__other-links'] {
      a {
        ${presets.font15_20()}
        opacity: .5;
        transition: all 300ms ease;

        &:hover {
          opacity: 1;
        }
      }
    }



    &[aria-labelledby='f__other-links'],
    &[aria-labelledby='f__social-media'] {
      grid-column: 5 / span 2;

      ${mq.greaterThan('tablet')} {
        grid-column: 10 / span 3;
      }
    }

    &[aria-labelledby='f__other-links'] {
      ${mq.greaterThan('tablet')} {
        grid-column: 10 / span 3;
      }
    }

    &[aria-labelledby='f__social-media'] {
      ${mq.greaterThan('tablet')} {
        grid-column: 6 / span 3;
      }
    }

    ul {
      align-items: flex-start;
      display: flex;
      flex-direction: column;
      gap: 10px;
      justify-content: flex-start;
    }

    a {
      ${createUnderline({})}
    }
  }

  .copyright,
  nav[aria-labelledby='f__social-media'],
  .btn--scroll-top {
    ${presets.font13_15()}
  }

  .copyright {
    white-space: nowrap;
  }

  .btn--scroll-top {
    display: none;
    align-items: center;
    color: var(--color-text);
    gap: 10px;
    justify-content: center;
    width: fit-content;

    ${mq.greaterThan('tablet')} {
      display: flex;
      grid-column: 10 / span 3;
    }

    &:hover {
      svg {
        transform: translateY(-4px);
      }
    }

    svg,
    span {
      flex-shrink: 0;
      white-space: nowrap;
    }

    svg {
      transition: transform 250ms ease;
    }
  }
`
